import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { 
  MousePointer2, 
  ArrowLeftRight, 
  Target, 
  Sparkles,
  Check,
  X,
  Zap,
  Rainbow
} from "lucide-react";

export function HowToPlayModal({ isOpen, onClose }) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg bg-card border-2 border-border max-h-[90vh] overflow-y-auto">
        <DialogHeader className="text-center">
          <DialogTitle className="font-display text-2xl sm:text-3xl font-bold text-foreground">
            How to Play 🍌
          </DialogTitle>
          
          <DialogDescription className="font-body text-base text-muted-foreground mt-1">
            Master the art of fruit crushing!
          </DialogDescription>
        </DialogHeader>

        {/* Instructions */}
        <div className="space-y-4 py-4">
          {/* Basic Steps */}
          <div className="flex gap-4 p-4 bg-muted rounded-xl">
            <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
              <MousePointer2 className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h3 className="font-display font-semibold text-foreground">1. Select a Fruit</h3>
              <p className="font-body text-sm text-muted-foreground mt-1">
                Click on any fruit to select it. The selected fruit will glow!
              </p>
            </div>
          </div>

          <div className="flex gap-4 p-4 bg-muted rounded-xl">
            <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
              <ArrowLeftRight className="w-6 h-6 text-accent" />
            </div>
            <div>
              <h3 className="font-display font-semibold text-foreground">2. Swap with Neighbor</h3>
              <p className="font-body text-sm text-muted-foreground mt-1">
                Click an adjacent fruit to swap. The swap must create a match of 3 or more!
              </p>
            </div>
          </div>

          <div className="flex gap-4 p-4 bg-muted rounded-xl">
            <div className="w-12 h-12 rounded-full bg-fruit-banana/20 flex items-center justify-center flex-shrink-0">
              <Target className="w-6 h-6 text-fruit-banana" />
            </div>
            <div>
              <h3 className="font-display font-semibold text-foreground">3. Collect Bananas</h3>
              <p className="font-body text-sm text-muted-foreground mt-1">
                Match bananas 🍌 to collect them. Get 30 bananas to win!
              </p>
            </div>
          </div>

          {/* Special Fruits Section */}
          <div className="p-4 bg-secondary rounded-xl border-2 border-accent/20">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-accent" />
              <h3 className="font-display font-semibold text-foreground">Special Fruits</h3>
            </div>
            
            {/* Striped Fruit */}
            <div className="flex gap-3 p-3 bg-card rounded-lg mb-2">
              <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0 relative overflow-hidden">
                <div className="absolute inset-0 flex flex-col justify-evenly">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="h-[2px] bg-accent/60" />
                  ))}
                </div>
                <span className="text-xl relative z-10">🍌</span>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-accent" />
                  <span className="font-display font-semibold text-sm">Striped Fruit</span>
                </div>
                <p className="font-body text-xs text-muted-foreground mt-1">
                  Match <strong>4 in a row</strong> to create. Clears entire row or column when matched!
                </p>
              </div>
            </div>
            
            {/* Disco Fruit */}
            <div className="flex gap-3 p-3 bg-card rounded-lg">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-red-200 via-yellow-200 to-blue-200 flex items-center justify-center flex-shrink-0">
                <span className="text-xl">🍈</span>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Rainbow className="w-4 h-4 text-primary" />
                  <span className="font-display font-semibold text-sm">Disco Melon</span>
                </div>
                <p className="font-body text-xs text-muted-foreground mt-1">
                  Match <strong>5 in a row</strong> to create. Swap with any fruit to clear ALL of that type!
                </p>
              </div>
            </div>
          </div>

          {/* Tips Box */}
          <div className="p-4 bg-muted rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-5 h-5 text-primary" />
              <h3 className="font-display font-semibold text-foreground">Pro Tips</h3>
            </div>
            <ul className="space-y-2 font-body text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                <span>Chain reactions give bonus points!</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                <span>Use striped fruits strategically to clear bananas</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                <span>Remaining moves give bonus points when you win</span>
              </li>
              <li className="flex items-start gap-2">
                <X className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
                <span>Invalid swaps do not cost moves</span>
              </li>
            </ul>
          </div>

          {/* Fruits Legend */}
          <div className="p-4 bg-muted rounded-xl">
            <h3 className="font-display font-semibold text-foreground mb-3">Fruits</h3>
            <div className="flex justify-around text-2xl">
              <div className="text-center">
                <span className="text-3xl">🍌</span>
                <p className="text-xs text-muted-foreground mt-1">Banana</p>
              </div>
              <div className="text-center">
                <span className="text-3xl">🍎</span>
                <p className="text-xs text-muted-foreground mt-1">Apple</p>
              </div>
              <div className="text-center">
                <span className="text-3xl">🍐</span>
                <p className="text-xs text-muted-foreground mt-1">Pear</p>
              </div>
              <div className="text-center">
                <span className="text-3xl">🍍</span>
                <p className="text-xs text-muted-foreground mt-1">Pineapple</p>
              </div>
              <div className="text-center">
                <span className="text-3xl">🍊</span>
                <p className="text-xs text-muted-foreground mt-1">Orange</p>
              </div>
            </div>
          </div>
        </div>

        {/* Close Button */}
        <Button 
          onClick={onClose}
          size="lg"
          className="w-full font-display font-semibold text-lg bg-primary hover:bg-primary/90 text-primary-foreground shadow-button"
        >
          Got it! Let us Play!
        </Button>
      </DialogContent>
    </Dialog>
  );
}
